const AJAX_HEADERS = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };

function formatAmount(amount) {
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

/**
 * `rate.deliveryDate` (see RateService::normalizeRate()) is eShipping's own
 * estimate for this offer, computed from the pickup date we sent it - which
 * already factors in ShippingSettings::preparationLeadDays (see
 * RateService::pickupDate()) - plus the carrier's transit time. So there's
 * nothing left to add on top of it here, just format it for display.
 * Parsed as Y/M/D components (not `new Date(isoDate)`) to avoid a UTC
 * midnight parse silently rolling back a day in a negative-UTC-offset
 * browser.
 */
function formatDeliveryDate(isoDate) {
    if (!isoDate) return null;

    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month || !day) return null;

    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
}

/**
 * HGES has no structured delivery-time field (only `deliveryDate`, a date,
 * and `transitTimeInDays`) - some carrier services instead bake a delivery
 * cutoff straight into their free-text name, e.g. "DHL EXPRESS WORLDWIDE
 * 12:00 EU" meaning delivery by noon. Pulled out here so it can be shown
 * next to the delivery date rather than left buried in the service name.
 */
function extractServiceTimeHint(serviceName) {
    const match = (serviceName || '').match(/\b([01]?\d|2[0-3]):([0-5]\d)\b/);
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : null;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function createSvgShape(tag, attributes) {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [name, value] of Object.entries(attributes)) {
        el.setAttribute(name, value);
    }
    return el;
}

function createStrokeShape(tag, attributes) {
    return createSvgShape(tag, {
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        ...attributes,
    });
}

function createIconSvg(className) {
    const svg = createSvgShape('svg', {
        width: '14',
        height: '14',
        viewBox: '0 0 24 24',
        fill: 'none',
        'aria-hidden': 'true',
        focusable: 'false',
    });
    svg.classList.add('rate-options__packaging-icon', className);
    return svg;
}

/**
 * Same package/box glyph used in the admin shipping rules screen (see
 * templates/admin/shipping.html.twig) - built as SVG nodes rather than
 * innerHTML so nothing here parses as markup.
 */
function createColisIcon() {
    const svg = createIconSvg('rate-options__packaging-icon--colis');

    svg.appendChild(createStrokeShape('path', {
        d: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z',
    }));
    svg.appendChild(createStrokeShape('path', {
        d: 'M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12',
    }));

    return svg;
}

/**
 * A slatted wooden crate sitting on a pallet (deck board + 3 support legs +
 * bottom skid) - visually distinct from createColisIcon() at a glance, so a
 * shopper can tell a palletized offer from an individual-carton one without
 * reading the text next to it.
 */
function createPalletIcon() {
    const svg = createIconSvg('rate-options__packaging-icon--palette');

    // Crate, with two slat lines so it doesn't read as a plain box.
    svg.appendChild(createStrokeShape('rect', { x: '5', y: '2', width: '14', height: '9', rx: '1' }));
    svg.appendChild(createStrokeShape('line', { x1: '5', y1: '5.5', x2: '19', y2: '5.5' }));
    svg.appendChild(createStrokeShape('line', { x1: '5', y1: '8.5', x2: '19', y2: '8.5' }));

    // Pallet: top deck board, three support legs, bottom skid.
    svg.appendChild(createStrokeShape('line', { x1: '2', y1: '14', x2: '22', y2: '14' }));
    [5, 12, 19].forEach((x) => {
        svg.appendChild(createStrokeShape('line', { x1: String(x), y1: '14', x2: String(x), y2: '20' }));
    });
    svg.appendChild(createStrokeShape('line', { x1: '2', y1: '20', x2: '22', y2: '20' }));

    return svg;
}

function createPackagingIcon(packagingMode, extraClassName) {
    const svg = 'palette' === packagingMode ? createPalletIcon() : createColisIcon();
    if (extraClassName) svg.classList.add(extraClassName);
    return svg;
}

// Selected carrier/rate for the current checkout form — there's only ever
// one checkout form on /cart, so module-level state is enough (no need to
// track it per-form).
let selectedRate = null;

// Every address field change fires its own fetchRates() call (see
// initCart's delegated listener) - several can be in flight at once while
// the shopper is still typing, and a slow early one (empty/partial address)
// can resolve after a later, complete one and wipe out good results with
// stale empty ones. This token makes renderRates() ignore any response
// that isn't from the most recent request.
let ratesRequestId = 0;

function updateCartBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count < 1;
    });
}

function showToast(type, message) {
    const toast = document.createElement('div');
    toast.className = `toast-notification toast-notification--${type}`;
    toast.setAttribute('data-toast', '');
    toast.innerHTML = `
        <span class="toast-notification__icon">${type === 'success' ? '✓' : '✕'}</span>
        <span class="toast-notification__message"></span>
        <button type="button" class="toast-notification__close" aria-label="Fermer">&times;</button>
    `;
    toast.querySelector('.toast-notification__message').textContent = message;
    document.body.appendChild(toast);

    const dismiss = () => {
        toast.classList.remove('is-visible');
        toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    };

    toast.querySelector('.toast-notification__close').addEventListener('click', dismiss);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(dismiss, 5000);
}

/**
 * Applies a cart-summary payload (from the update/remove endpoints) to the
 * /cart page: per-line totals, the grand total, and the empty state —
 * without a reload.
 */
function applyCartSummary(data) {
    const itemsById = new Map(data.items.map((item) => [String(item.productId), item]));

    document.querySelectorAll('[data-cart-row]').forEach((row) => {
        const productId = row.getAttribute('data-cart-row');
        const item = itemsById.get(productId);

        if (!item) {
            row.remove();
            return;
        }

        const lineTotalEl = row.querySelector(`[data-cart-line-total="${productId}"]`);
        if (lineTotalEl) {
            lineTotalEl.textContent = `${formatAmount(item.lineTotal)} €`;
        }
    });

    const totalEl = document.querySelector('[data-cart-total]');
    if (totalEl) {
        totalEl.textContent = `${formatAmount(data.total)} €`;
        totalEl.dataset.cartTotalValue = data.total;
    }
    updateGrandTotal();

    const itemsContainer = document.querySelector('[data-cart-items]');
    const emptyContainer = document.querySelector('[data-cart-empty]');

    if (itemsContainer) itemsContainer.hidden = data.empty;
    // Two separate elements now carry data-cart-summary - the totals block
    // (left column) and the checkout form (right column) - both need to
    // hide/show together when the cart empties out or fills back up.
    document.querySelectorAll('[data-cart-summary]').forEach((el) => { el.hidden = data.empty; });
    if (emptyContainer) emptyContainer.hidden = !data.empty;
}

async function submitCartForm(form) {
    const action = form.getAttribute('data-cart-form');
    const body = new FormData(form);

    let response;
    try {
        response = await fetch(form.action, { method: 'POST', body, headers: AJAX_HEADERS });
    } catch {
        form.submit();
        return;
    }

    let data;
    try {
        data = await response.json();
    } catch {
        form.submit();
        return;
    }

    if (response.status === 409 && data.redirect) {
        window.location.href = data.redirect;
        return;
    }

    if (!data.success) {
        showToast('error', data.message ?? 'Une erreur est survenue.');
        return;
    }

    updateCartBadge(data.cartCount);

    if (action === 'add') {
        showToast('success', data.message);
        return;
    }

    // 'update' or 'remove' — data carries the full cart summary.
    applyCartSummary(data);

    // The bottle count driving both the feasibility quota and the
    // packaging/weight behind any already-fetched transport rate just
    // changed - re-running only the feasibility check left a stale
    // Transport/Assurance price on screen after a quantity change.
    const checkoutForm = document.querySelector('.cart__checkout-form');
    if (checkoutForm) {
        checkFeasibility(checkoutForm);
        fetchRates(checkoutForm);
    }
}

const PICKUP_VALUE = 'Enlèvement au domaine';

// Address fields required for a SHIPPING order only - see
// CheckoutDto::validateDeliveryFields(). addressLine2 is deliberately left
// out, it stays optional either way.
const SHIPPING_REQUIRED_FIELDS = ['addressLine1', 'postalCode', 'city', 'country'];

// Billing fields required whenever they're actually collected - a PICKUP
// order always collects them, a SHIPPING order only when "same as shipping"
// is unchecked (see applyBillingToggle()). billingAddressLine2 stays
// optional either way, same reasoning as above.
const BILLING_REQUIRED_FIELDS = ['billingRecipientName', 'billingAddressLine1', 'billingPostalCode', 'billingCity', 'billingCountry'];

/**
 * Toggles the shipping-address fields for a checkout form based on the
 * selected delivery method radio. `disabled` (not just `hidden`) is what
 * keeps stale values out of the submit. `required` is kept in sync the same
 * way (flipped for phone, which is mandatory for pickup only) - it both
 * drives native browser validation and the required-field asterisk (see
 * _cart.scss's `label:has(+ [required])`).
 */
function applyDeliveryMethod(form) {
    const checked = form.querySelector('[data-delivery-method-toggle] input:checked');
    const isPickup = checked?.value === PICKUP_VALUE;

    form.querySelectorAll('[data-shipping-only]').forEach((wrapper) => {
        wrapper.hidden = isPickup;
        wrapper.querySelectorAll('input, select').forEach((field) => {
            field.disabled = isPickup;
        });
    });

    SHIPPING_REQUIRED_FIELDS.forEach((name) => {
        const field = form.querySelector(`[name$="[${name}]"]`);
        if (field) field.required = !isPickup;
    });

    const phone = form.querySelector('[name$="[phone]"]');
    if (phone) {
        phone.required = isPickup;
    }

    applyBillingToggle(form);
}

/**
 * Toggles the rest of the checkout form ([data-destination-gated] -
 * everything from the recipient name down to the submit button) and the
 * "destination not open" notice based on the last feasibility check. A
 * PICKUP order has no destination country to gate on, so it's always shown.
 */
function applyDestinationGate(form, { isPickup, destinationClosed }) {
    const gatedSection = form.querySelector('[data-destination-gated]');
    const closedMessageEl = form.querySelector('[data-destination-closed-message]');

    if (gatedSection) gatedSection.hidden = !isPickup && destinationClosed;
    if (closedMessageEl) closedMessageEl.hidden = isPickup || !destinationClosed;
}

/**
 * Calls the /rates/feasability check as soon as the destination country or
 * delivery method are known. Every way a SHIPPING destination can come back
 * infeasible (blocked in the back-office, or eShipping itself rejecting it -
 * see FeasibilityService) is a dead end the shopper can't fix by editing the
 * form, so it always hides the rest of it behind [data-destination-gated]
 * and points them to hello@honorie.fr instead - there's no "form stays
 * visible but blocked" case left. The server re-checks this on submit
 * (CartController::checkout) - this is UX only, so a fetch failure just
 * leaves the form/button as they were.
 */
async function checkFeasibility(form) {
    const submitButton = form.querySelector('[data-checkout-submit]');
    const countryField = form.querySelector('[name$="[country]"]');
    const deliveryField = form.querySelector('[data-delivery-method-toggle] input:checked');

    if (!submitButton || !countryField) {
        return;
    }

    const isPickup = deliveryField?.value === PICKUP_VALUE;

    if (isPickup) {
        submitButton.disabled = false;
        applyDestinationGate(form, { isPickup: true, destinationClosed: false });
        return;
    }

    if ('' === countryField.value.trim()) {
        // Nothing chosen yet - nothing to check, nothing to show below it.
        submitButton.disabled = true;
        applyDestinationGate(form, { isPickup: false, destinationClosed: false });
        return;
    }

    const body = new URLSearchParams({
        country: countryField.value,
        deliveryMethod: deliveryField?.value ?? '',
    });

    let data;
    try {
        const response = await fetch(form.getAttribute('data-feasibility-url'), { method: 'POST', body, headers: AJAX_HEADERS });
        data = await response.json();
    } catch {
        return;
    }

    applyDestinationGate(form, { isPickup: false, destinationClosed: !data.feasible });
    submitButton.disabled = !data.feasible;
}

/**
 * Recomputes the displayed grand total from the product subtotal (kept in
 * data-cart-total-value, see applyCartSummary) plus the currently selected
 * rate's costs, shown as three distinct rows rather than folded into one
 * "Transport" figure — mirrors the breakdown _order_summary.html.twig already
 * shows post-checkout (Frais de port / Assurance / Autres frais), so the
 * shopper sees the same breakdown before and after placing the order. The
 * "HT"/"TTC" suffix is shown once, on the "Total" row's label (see
 * cart.html.twig) rather than repeated on every amount here.
 */
function updateGrandTotal() {
    const subtotalEl = document.querySelector('[data-cart-total]');
    const grandTotalEl = document.querySelector('[data-cart-grand-total]');
    const shippingRow = document.querySelector('[data-cart-shipping-row]');
    const shippingTotalEl = document.querySelector('[data-cart-shipping-total]');
    const insuranceRow = document.querySelector('[data-cart-insurance-row]');
    const insuranceTotalEl = document.querySelector('[data-cart-insurance-total]');
    const otherCostsRow = document.querySelector('[data-cart-other-costs-row]');
    const otherCostsTotalEl = document.querySelector('[data-cart-other-costs-total]');
    const otherCostsBreakdownEl = document.querySelector('[data-cart-other-costs-breakdown]');
    if (!subtotalEl || !grandTotalEl) return;

    const subtotal = parseFloat(subtotalEl.dataset.cartTotalValue ?? '0') || 0;

    if (!selectedRate) {
        grandTotalEl.textContent = subtotalEl.textContent;
        if (shippingRow) shippingRow.hidden = true;
        if (insuranceRow) insuranceRow.hidden = true;
        if (otherCostsRow) otherCostsRow.hidden = true;
        return;
    }

    const insuranceChecked = document.querySelector('[name$="[insured]"]')?.checked ?? false;
    const insuranceCost = insuranceChecked ? (selectedRate.insuranceCostAllIn ?? 0) : 0;
    const shippingTotal = selectedRate.shippingCostAllIn + selectedRate.otherCostsAllIn + insuranceCost;

    if (shippingRow && shippingTotalEl) {
        shippingRow.hidden = false;
        shippingTotalEl.textContent = `${formatAmount(selectedRate.shippingCostAllIn)} €`;
    }
    if (insuranceRow && insuranceTotalEl) {
        insuranceRow.hidden = insuranceCost <= 0;
        insuranceTotalEl.textContent = `${formatAmount(insuranceCost)} €`;
    }
    if (otherCostsRow && otherCostsTotalEl) {
        otherCostsRow.hidden = selectedRate.otherCostsAllIn <= 0;
        otherCostsTotalEl.textContent = `${formatAmount(selectedRate.otherCostsAllIn)} €`;

        // Names each cost bundled into "Autres frais" (e.g. "Représentation
        // fiscale et douane") instead of leaving the shopper to guess what
        // it covers - see RateService::normalizeRate()'s `otherCosts`.
        if (otherCostsBreakdownEl) {
            otherCostsBreakdownEl.textContent = (selectedRate.otherCosts ?? [])
                .map((cost) => `${cost.label} : ${formatAmount(cost.amountAllIn)} €`)
                .join(' · ');
        }
    }

    grandTotalEl.textContent = `${formatAmount(subtotal + shippingTotal)} €`;
}

/**
 * Applies the shopper's pick among the fetched rates: fills the hidden
 * rateChecksum field the server re-validates on submit (see
 * CartController::checkout()), and shows the insurance checkbox only when
 * the chosen offer actually has one (insuranceCostAllIn is null otherwise).
 */
function selectRate(form, rate) {
    selectedRate = rate;

    const checksumField = form.querySelector('[name$="[rateChecksum]"]');
    if (checksumField) checksumField.value = rate.checksum;

    const insuranceField = form.querySelector('[data-insurance-field]');
    const insurancePriceEl = form.querySelector('[data-insurance-price]');
    if (insuranceField) {
        insuranceField.hidden = null === rate.insuranceCostAllIn;
    }
    if (insurancePriceEl && null !== rate.insuranceCostAllIn) {
        insurancePriceEl.textContent = `(+${formatAmount(rate.insuranceCostAllIn)} €)`;
    }

    updateGrandTotal();
}

/**
 * Undoes selectRate() - clears the hidden rateChecksum field and hides the
 * insurance checkbox. Called whenever there's no longer a selected rate to
 * back them (renderRates() with an empty list, or before a fresh list
 * renders) - otherwise a rate picked for a previous address stays in the
 * hidden field and would get submitted against the shopper's new one.
 */
function clearSelectedRate(form) {
    const checksumField = form.querySelector('[name$="[rateChecksum]"]');
    if (checksumField) checksumField.value = '';

    const insuranceField = form.querySelector('[data-insurance-field]');
    const insuranceCheckbox = form.querySelector('[name$="[insured]"]');
    if (insuranceField) insuranceField.hidden = true;
    if (insuranceCheckbox) insuranceCheckbox.checked = false;
}

/**
 * Toggles the Honorie-wordmark loader (see _logo_loader.html.twig) shown
 * while fetchRates() is waiting on the /rates call - swapped for the actual
 * offer list (or the "no offers" message) by hideRatesLoader(), called from
 * renderRates() and from fetchRates()'s error path.
 */
function showRatesLoader(form) {
    const loader = form.querySelector('[data-rates-loader]');
    const list = form.querySelector('[data-rates-list]');
    const messageEl = form.querySelector('[data-rates-message]');
    if (loader) loader.hidden = false;
    if (list) list.hidden = true;
    if (messageEl) messageEl.hidden = true;
}

function hideRatesLoader(form) {
    const loader = form.querySelector('[data-rates-loader]');
    const list = form.querySelector('[data-rates-list]');
    if (loader) loader.hidden = true;
    if (list) list.hidden = false;
}

/**
 * Builds one `<label class="rate-options__option">` for a single rate offer
 * (radio + carrier/price + delivery date + packaging recap) - split out of
 * renderRates() so it can be reused across the palette/colis group sections.
 */
function buildRateOption(form, rate, isDefault) {
    const label = document.createElement('label');
    label.className = 'rate-options__option';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'rateOption';
    radio.value = rate.checksum;
    if (isDefault) radio.checked = true;

    const textWrap = document.createElement('span');
    textWrap.className = 'rate-options__text';

    const details = document.createElement('span');
    details.textContent = `${rate.serviceName || rate.carrier} : ${formatAmount(rate.shippingCostAllIn + rate.otherCostsAllIn)} €`;
    textWrap.appendChild(details);

    const deliveryDate = formatDeliveryDate(rate.deliveryDate);
    if (deliveryDate) {
        const deliveryEl = document.createElement('span');
        deliveryEl.className = 'rate-options__delivery';
        const timeHint = extractServiceTimeHint(rate.serviceName);
        deliveryEl.textContent = timeHint
            ? `Livraison estimée le ${deliveryDate} avant ${timeHint}`
            : `Livraison estimée le ${deliveryDate}`;
        textWrap.appendChild(deliveryEl);
    }

    if (rate.packagingSummary) {
        const packagingEl = document.createElement('span');
        packagingEl.className = 'rate-options__packaging';
        packagingEl.appendChild(createPackagingIcon(rate.packagingMode));
        const packagingText = document.createElement('span');
        packagingText.textContent = rate.packagingSummary;
        packagingEl.appendChild(packagingText);
        textWrap.appendChild(packagingEl);
    }

    label.appendChild(radio);
    label.appendChild(textWrap);

    radio.addEventListener('change', () => selectRate(form, rate));

    return label;
}

// Groups shown above the offer lists, in this order - a shopper scanning the
// page sees pallet offers (usually cheaper for large orders) first, each
// under its own heading + oversized icon so the two shipping modes read as
// clearly distinct sections rather than one flat, hard-to-scan list.
const RATE_GROUPS = [
    { mode: 'palette', label: 'Livraison en palette' },
    { mode: 'colis', label: 'Livraison en colis' },
];

function renderRates(form, rates) {
    const list = form.querySelector('[data-rates-list]');
    const messageEl = form.querySelector('[data-rates-message]');
    if (!list) return;

    hideRatesLoader(form);
    list.innerHTML = '';
    selectedRate = null;
    clearSelectedRate(form);
    updateGrandTotal();

    if (rates.length === 0) {
        if (messageEl) {
            messageEl.textContent = "Aucune offre de transport disponible pour cette adresse pour le moment.";
            messageEl.hidden = false;
        }
        return;
    }

    if (messageEl) messageEl.hidden = true;

    RATE_GROUPS.forEach(({ mode, label }) => {
        const groupRates = rates.filter((rate) => (rate.packagingMode || 'colis') === mode);
        if (groupRates.length === 0) return;

        const section = document.createElement('div');
        section.className = 'rate-options__group';

        const heading = document.createElement('div');
        heading.className = 'rate-options__group-heading';
        heading.appendChild(createPackagingIcon(mode, 'rate-options__group-icon'));
        const headingText = document.createElement('span');
        headingText.textContent = label;
        heading.appendChild(headingText);
        section.appendChild(heading);

        const optionsList = document.createElement('div');
        optionsList.className = 'rate-options__group-options';
        groupRates.forEach((rate) => {
            optionsList.appendChild(buildRateOption(form, rate, rate === rates[0]));
        });
        section.appendChild(optionsList);

        list.appendChild(section);
    });

    selectRate(form, rates[0]);
}

/**
 * Fetches carrier/rate offers for the current address + delivery method
 * (see CartController::getRates()) as soon as enough of the shipping
 * address is known — mirrors checkFeasibility()'s trigger points, but reads
 * the full address rather than just the country since /rates needs it.
 * Skipped without a country (nothing to quote yet - also avoids spamming
 * the backend on page load for a guest with a blank form, see initCart) or
 * while [data-destination-gated] is hidden (destination not open - see
 * checkFeasibility/applyDestinationGate).
 */
async function fetchRates(form) {
    const deliveryField = form.querySelector('[data-delivery-method-toggle] input:checked');
    const url = form.getAttribute('data-rates-url');
    const countryField = form.querySelector('[name$="[country]"]');
    const gatedSection = form.querySelector('[data-destination-gated]');
    if (!url || deliveryField?.value === PICKUP_VALUE || !countryField?.value.trim() || gatedSection?.hidden) {
        return;
    }

    const field = (name) => form.querySelector(`[name$="[${name}]"]`)?.value ?? '';
    const body = new URLSearchParams({
        deliveryMethod: deliveryField?.value ?? '',
        recipientName: field('recipientName'),
        addressLine1: field('addressLine1'),
        addressLine2: field('addressLine2'),
        postalCode: field('postalCode'),
        city: field('city'),
        country: field('country'),
        phone: field('phone'),
    });

    const requestId = ++ratesRequestId;
    showRatesLoader(form);

    let data;
    try {
        const response = await fetch(url, { method: 'POST', body, headers: AJAX_HEADERS });
        data = await response.json();
    } catch {
        if (requestId === ratesRequestId) hideRatesLoader(form);
        return;
    }

    // A newer request already started (and may have already resolved) -
    // this response is stale, drop it instead of overwriting the current UI.
    if (requestId !== ratesRequestId) {
        return;
    }

    renderRates(form, data.rates ?? []);
}

/**
 * Pickup has no shipping address to copy, so its billing address block is
 * always shown and its "same as shipping" checkbox is hidden/disabled by the
 * [data-shipping-only] handling above — this only needs to show/hide the
 * billing fields for a SHIPPING order based on that checkbox.
 */
function applyBillingToggle(form) {
    const checkedDelivery = form.querySelector('[data-delivery-method-toggle] input:checked');
    const isPickup = checkedDelivery?.value === PICKUP_VALUE;
    const sameAsShipping = form.querySelector('[data-billing-toggle] input');
    const fields = form.querySelector('[data-billing-fields]');
    if (!fields) {
        return;
    }

    const hideFields = !isPickup && (sameAsShipping?.checked ?? false);
    fields.hidden = hideFields;
    fields.querySelectorAll('input, select').forEach((field) => {
        field.disabled = hideFields;
    });

    BILLING_REQUIRED_FIELDS.forEach((name) => {
        const field = form.querySelector(`[name$="[${name}]"]`);
        if (field) field.required = !hideFields;
    });
}

function initCart() {
    // Runs the same checks a field change would trigger - a logged-in
    // customer's default address (see CartController::buildCheckoutForm)
    // can arrive already filled in, including the country, so the gate and
    // rate list need to reflect that from the first paint rather than
    // waiting for the shopper to touch a field.
    document.querySelectorAll('.cart__checkout-form').forEach((form) => {
        applyDeliveryMethod(form);
        checkFeasibility(form);
        fetchRates(form);
    });

    document.addEventListener('change', (event) => {
        const radio = event.target.closest('[data-delivery-method-toggle] input');
        if (radio) {
            const form = radio.closest('form');
            applyDeliveryMethod(form);
            checkFeasibility(form);
            fetchRates(form);
            return;
        }

        const billingCheckbox = event.target.closest('[data-billing-toggle] input');
        if (billingCheckbox) {
            applyBillingToggle(billingCheckbox.closest('form'));
            return;
        }

        const insuredCheckbox = event.target.closest('[name$="[insured]"]');
        if (insuredCheckbox) {
            updateGrandTotal();
            return;
        }

        const countryField = event.target.closest('[name$="[country]"]');
        if (countryField) {
            checkFeasibility(countryField.closest('form'));
            fetchRates(countryField.closest('form'));
            return;
        }

        // Any other shipping-address field (addressLine1, postalCode, city,
        // recipientName, phone) also needs a fresh rate fetch - the country
        // field above gets its own branch only because it also drives
        // checkFeasibility(). Excludes the rate radios themselves - they
        // live inside this same [data-shipping-only] section (the Transport
        // block), so picking a different offer bubbled up here too and
        // triggered an immediate re-fetch that reset the list straight back
        // to the first option (see selectRate()'s own listener for that).
        const addressField = event.target.closest('[data-shipping-only] input, [data-shipping-only] select');
        if (addressField && !addressField.closest('[data-rates-list]')) {
            fetchRates(addressField.closest('form'));
        }
    });

    // Quantity changes already auto-submit (below) — this button only
    // matters as a no-JS fallback, so hide it once JS is confirmed running.
    document.querySelectorAll('[data-cart-form="update"] .cart__item-update').forEach((button) => {
        button.hidden = true;
    });

    document.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-cart-form]');
        if (!form) {
            return;
        }

        event.preventDefault();
        submitCartForm(form);
    });

    // Recomputes the subtotal as soon as a cart line's quantity changes
    // (selector buttons or manual entry) instead of waiting for the
    // fallback "Mettre à jour" submit — see [data-cart-form="update"].
    document.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (!input) {
            return;
        }

        const form = input.closest('[data-cart-form="update"]');
        if (form) {
            form.requestSubmit();
        }
    });
}

export default initCart;
